import React, { FC, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { IModalAction } from 'transfix-ui/components/Modals'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import Grid from 'transfix-ui/core/Grid'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { MarketplaceDataUpdateFormBody } from './MarketplaceDataUpdateFormBody'
import { TUpdateMarketplaceDataFormSchema } from './UpdateMarketplaceDataFormSchema'
import { useUpdateMarketplaceDataMutation } from './graphql/UpdateMarketplaceDataMutation'
import { useUpdateMarketplaceDataForm } from './useUpdateMarketplaceDataForm'

interface IUpdateMarketplaceDataModalProps extends Pick<IFormModalProps, 'onClose'> {
  carrierApplicationId: string
}

export const UpdateMarketplaceDataModal: FC<IUpdateMarketplaceDataModalProps> = ({
  carrierApplicationId,
  onClose,
}) => {
  const { trackEvent } = useMPAnalytics()
  // Mutation Errors
  const [mutationErrors, setMutationErrors] = useState<string[]>([])
  const formMethods = useUpdateMarketplaceDataForm({})
  const { control, handleSubmit, watch } = formMethods
  const formState = watch(['shouldSyncPhysicalAddress'])
  const showPhysicalAddressNote = formState?.shouldSyncPhysicalAddress

  const [updateMarketplaceData, { loading }] = useUpdateMarketplaceDataMutation()
  const { openToast } = useGlobalToast()

  const updateMarketplaceDataHandler = async (formValues: TUpdateMarketplaceDataFormSchema) => {
    setMutationErrors([])

    const {
      shouldSyncContactEmail,
      shouldSyncContactName,
      shouldSyncContactPhone,
      shouldSyncDbaName,
      shouldSyncEin,
      shouldSyncLegalName,
      shouldSyncMcNumber,
      shouldSyncPhysicalAddress,
    } = formValues

    const { data } = await updateMarketplaceData({
      variables: {
        input: {
          carrierApplicationId,
          shouldSyncMcNumber,
          shouldSyncEin,
          shouldSyncLegalName,
          shouldSyncDbaName,
          shouldSyncPhysicalAddress,
          shouldSyncContactName,
          shouldSyncContactEmail,
          shouldSyncContactPhone,
        },
      },
    })

    const { syncHighwayDataIntoCarrier } = data || {}
    const { errors = [] } = syncHighwayDataIntoCarrier || {}

    if (errors.length > 0) {
      const errorLogs = errors.map(error => error.message)
      setMutationErrors(errorLogs)
      trackEvent('Carrier', 'HIGHWAY_MANUAL_SYNC_FAILURE', { message: errorLogs })
      return
    }

    trackEvent('Carrier', 'HIGHWAY_MANUAL_SYNC_SUCCESS')

    openToast({
      toastType: 'default',
      toastMessage: 'Marketplace successfully updated with Highway data',
    })
    onClose()
  }

  const actions: IModalAction[] = [
    {
      label: 'Cancel',
      type: 'dismiss',
      action: onClose,
      disabled: loading,
    },
    {
      label: 'Submit',
      type: 'confirm',
      action: handleSubmit(updateMarketplaceDataHandler),
      disabled: loading,
    },
  ]

  return (
    <FormModal
      open
      isClose
      title='Update Marketplace Data'
      actions={actions}
      size={7}
      error={[...mutationErrors]}
      onClose={onClose}
      dataTest='update-mp-data-modal'>
      <div>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormProvider {...formMethods}>
              <MarketplaceDataUpdateFormBody
                control={control}
                showPhysicalAddressNote={showPhysicalAddressNote}
              />
            </FormProvider>
          </Grid>
        </Grid>
      </div>
    </FormModal>
  )
}
